import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "./components/App";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { getToken, initNaa } from "./auth";
import { MemoryRouter } from "react-router-dom";
import { AppProvider } from "./context/AppContext";

const title = "Elysia Assistant Add-in";

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;
let emailData = {};
let mode;
let isDarkTheme = false;
let isMobile = false;

Office.onReady(async (info) => {
  if (info.host) {
    console.log("Office initialized:", info.host, info.platform);
    await initializeAddin();
  }
  root?.render(
    <MemoryRouter>
      <FluentProvider theme={webLightTheme}>
        <AppProvider emailData={emailData} mode={mode} isDarkMode={isDarkTheme} isMobile={isMobile}>
          <App title={title} />
        </AppProvider>
      </FluentProvider>
    </MemoryRouter>
  );
});

const initializeAddin = async () => {
  try {
    emailData = await loadEmailContext();
    mode = await detectAddinMode();
    isMobile = isMobileDevice();
    if (isMobile) {
      isDarkTheme = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
    } else {
      isDarkTheme = isOutlookDarkMode();
    }
  } catch (error) {
    console.error("Error initializing add-in:", error);
    mode = "reply";
  }
};

function loadEmailContext(): Promise<any> {
  return new Promise((resolve, reject) => {
    try {
      const item = Office.context.mailbox.item;

      if (!item) {
        console.error("No email item selected");
        reject(new Error("No email item selected"));
        return;
      }

      // Get the email body
      item.body.getAsync(
        Office.CoercionType.Text,
        { bodyMode: Office.MailboxEnums.BodyMode.FullBody },
        (bodyResult) => {
          if (bodyResult.status === Office.AsyncResultStatus.Succeeded) {
            const data = {
              itemId: item.itemId,
              conversationId: item.conversationId,
              subject: item.subject,
              from: item.from
                ? {
                    displayName: item.from.displayName,
                    emailAddress: item.from.emailAddress,
                  }
                : null,
              to: Array.isArray(item.to)
                ? item.to.map((recipient) => ({
                    displayName: recipient.displayName,
                    emailAddress: recipient.emailAddress,
                  }))
                : [],
              cc: Array.isArray(item.cc)
                ? item.cc.map((recipient) => ({
                    displayName: recipient.displayName,
                    emailAddress: recipient.emailAddress,
                  }))
                : [],
              dateTimeCreated: item.dateTimeCreated,
              body: bodyResult.value,
              bodyType: "text",
            };

            resolve(data); // Resolve with the data
          } else {
            reject(new Error("Failed to get email body"));
          }
        }
      );
    } catch (error) {
      reject(error);
    }
  });
}

const detectAddinMode = async (): Promise<string> => {
  try {
    if (
      Office.context.mailbox?.item?.getComposeTypeAsync &&
      typeof Office.context.mailbox.item.getComposeTypeAsync === "function"
    ) {
      return new Promise((resolve) => {
        Office.context.mailbox.item.getComposeTypeAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const composeType = result.value.composeType;

            switch (composeType) {
              case Office.MailboxEnums.ComposeType.NewMail:
                resolve("compose");
                break;
              case Office.MailboxEnums.ComposeType.Reply:
                resolve("reply");
                break;
              default:
                resolve("reply");
            }
          } else {
            resolve("reply");
          }
        });
      });
    } else {
      console.log("Add-in opened in read mode");
      return "reply";
    }
  } catch (error) {
    console.log("Defaulting to read mode");
    return "reply";
  }
};

const isOutlookDarkMode = () => {
  const theme = Office.context.officeTheme;

  if (!theme) return false;
  const bodyBackground = theme.bodyBackgroundColor || "#FFFFFF";
  const numericColor = parseInt(bodyBackground.replace("#", ""), 16);
  return numericColor < 0x888888;
};

const isMobileDevice = () => {
  if (
    Office.context.platform === Office.PlatformType.iOS ||
    Office.context.platform === Office.PlatformType.Android
  ) {
    return true;
  } else {
    return false;
  }
};
if ((module as any).hot) {
  (module as any).hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    root?.render(NextApp);
  });
}
